import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { ModalConfirmation, UiBreadcrumb, UiCard, UiCardBody, UiCardHeader, UiDataTable } from 'components/common'
import { Box, Button, Flex, FormControl, FormLabel, Heading, Select, Text, useDisclosure } from '@chakra-ui/react'
import { deleteArtworkRequest, getAdhocQuoteByNumber, getArtworkRequests, getEdgeQuoteByNumber, getUsersByRoles } from 'api'
import { ARTWORK_REQUEST_STATUSES, getAssetUrl, ROLES } from 'lib'
import { format as formatDate } from 'date-fns'
import { useAppContext } from 'hooks/useAppContext'

const BreadcrumbNav = [
  {
    label: 'Artwork Request',
    path: '/artwork-requests/queue'
  },
  {
    label: 'Queue',
    isCurrentPage: true
  }
]

const ARTWORK_REQUEST_COLUMNS = [
  {
    label: 'Status',
    orderBy: 'status',
    render: ({ status }) => {
      switch (status) {
        case ARTWORK_REQUEST_STATUSES.NotYetStarted:
          return <Text color='red'>{status}</Text>
        case ARTWORK_REQUEST_STATUSES.ReadyForReview:
          return <Text color='green'>{status}</Text>
        default:
          return status
      }
    }
  },
  {
    label: 'Priority',
    orderBy: 'priority',
    render: ({ priority }) => priority
  },
  {
    label: 'Quote Number',
    orderBy: 'quoteNumber',
    render: ({ quoteNumber }) => quoteNumber || 'None'
  },
  {
    label: 'Project Name',
    orderBy: 'projectName',
    render: ({ projectName }) => projectName
  },
  {
    label: 'Status',
    orderBy: 'status',
    render: ({ status }) => status
  },
  {
    label: 'Sales User',
    orderBy: 'salesUserName',
    render: ({ salesUserName }) => salesUserName
  },
  {
    label: 'Graphic Designer',
    orderBy: 'assignedGraphicDesignerName',
    render: ({ assignedGraphicDesignerName }) => assignedGraphicDesignerName
  },
  {
    label: 'Created at',
    orderBy: 'createdAt',
    render: ({ createdAt }) => formatDate(new Date(createdAt), 'MM/dd/yyyy')
  },
  {
    label: 'hrs to be first opened',
    orderBy: 'secondsFirstOpened',
    render: ({ secondsFirstOpened }) => secondsFirstOpened ? (secondsFirstOpened / 3600).toFixed(1) + 'hrs' : 'Not opened yet'
  },
  {
    label: 'hrs to be completed',
    orderBy: 'secondsCompleted',
    render: ({ secondsCompleted }) => secondsCompleted ? (secondsCompleted / 3600).toFixed(1) + 'hrs' : 'Not completed yet'
  },
  {
    label: 'hrs between first opened to completed',
    orderBy: 'secondsBetweenFirstOpenedToCompleted',
    render: ({ secondsBetweenFirstOpenedToCompleted }) => secondsBetweenFirstOpenedToCompleted ? (secondsBetweenFirstOpenedToCompleted / 3600).toFixed(1) + 'hrs' : 'Not completed yet'
  },
  {
    label: 'Actions',
    orderBy: null,
    render: ({ id, quoteNumber, currentUser, salesUserId, onRemove, onView, onOpenQuotePDF }) => {
      return (
        <Flex columnGap={2}>
          {quoteNumber > 0 && <Button size="xs" colorScheme="gray" onClick={() => onOpenQuotePDF(quoteNumber)}>Quote PDF</Button>}
          <Button size="xs" colorScheme="blue" onClick={() => onView(id)}>View</Button>
          {(currentUser?.role === ROLES.Admin || currentUser?.role === ROLES.SuperUser || currentUser?.id === salesUserId) && <Button size="xs" colorScheme="red" onClick={() => onRemove(id)}>Delete</Button>}
        </Flex>
      )
    }
  }
]

// salespersonId required when the component is used in dashboard
export const ArtworkRequestQueue = () => {
  const { createToast, currentUser } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 50,
    search: '',
    orderBy: {
      key: 'priority',
      direction: 'asc'
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [graphicDesigners, setGraphicDesigners] = useState([])
  const [requests, setRequests] = useState([])
  const [filterRequests, setFilterRequests] = useImmer({
    status: 'Open',
    graphicDesignerId: currentUser?.role === ROLES.GraphicDesigner ? currentUser?.id : 'All'
  })
  const disclosureConfirmation = useDisclosure()
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [isRemoving, setIsRemoving] = useState(false)

  useEffect(() => {
    const loadGraphicDesigners = async () => {
      const response = await getUsersByRoles(1, 9999999, '', 'name', 'asc', [ROLES.GraphicDesigner, ROLES.SeniorGraphicDesigner])
      setGraphicDesigners(response.rows)
    }
    loadGraphicDesigners()
  }, [])

  const load = async () => {
    setIsLoading(true)
    try {
      const response = await getArtworkRequests(
        tableConfig.curPage,
        tableConfig.itemsPerPage,
        tableConfig.search,
        tableConfig.orderBy.key,
        tableConfig.orderBy.direction,
        filterRequests.status,
        filterRequests.graphicDesignerId
      )
      setRequests(response.rows)
      setTableConfig(draft => {
        draft.totalPages = response.totalPages
        draft.totalRecords = response.totalRecords
      })
      setIsLoading(false)
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [filterRequests.status, filterRequests.graphicDesignerId, tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  const onViewRequest = (requestId) => {
    window.open(`/artwork-requests/view/${requestId}`, '_blank')
  }

  const onOpenQuotePDF = async (quoteNumber) => {
    try {
      const quote = await getEdgeQuoteByNumber(quoteNumber)
      if (quote && quote.pdf) {
        window.open(getAssetUrl(quote.pdf), '_blank')
        return
      }
    } catch (e) {
    }

    try {
      const quote = await getAdhocQuoteByNumber(quoteNumber)
      if (quote && quote.pdf) {
        window.open(getAssetUrl(quote.pdf), '_blank')
        return
      }
    } catch (e) {
    }

    createToast('info', 'Info', 'Quote number is not correct')
  }

  const onRemove = (requestId) => {
    setSelectedRequest({ id: requestId })
    disclosureConfirmation.onOpen()
  }

  const handleRemove = async (requestId) => {
    setIsRemoving(true)
    try {
      await deleteArtworkRequest(requestId)
      setIsRemoving(false)
      disclosureConfirmation.onClose()
      createToast('success', 'Success', 'Artwork request removed')
      load()
    } catch (e) {
      createToast('error', 'Error', 'Failed to remove request')
      setIsRemoving(false)
    }
  }

  const handleChange = (e) => {
    setFilterRequests(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const customizedRequests = useMemo(() => {
    return requests.map(v => {
      return { ...v, onView: onViewRequest, onOpenQuotePDF, onRemove, currentUser }
    })
  }, [requests])

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Artwork Queue</Heading>
        </Flex>
        <UiCard borderRadius={0}>
          <UiCardHeader bg="gray.500" p={4}>
            <Text fontWeight="bold" color="white">Artwork Requests</Text>
          </UiCardHeader>
          <UiCardBody>
            <Flex gap={4} alignItems="flex-end" mb={8}>
              <FormControl flex={1}>
                <FormLabel>Project Status</FormLabel>
                <Select name="status" value={filterRequests.status} onChange={handleChange}>
                  <option value="All">All</option>
                  <option value="Open">Open</option>
                  {Object.values(ARTWORK_REQUEST_STATUSES).map(status => <option key={status} value={status}>{status}</option>)}
                </Select>
              </FormControl>
              {currentUser?.role !== ROLES.GraphicDesigner && (
                <FormControl flex={1}>
                  <FormLabel>Graphic Designer</FormLabel>
                  <Select name="graphicDesignerId" value={filterRequests.graphicDesignerId} onChange={handleChange}>
                    <option value="All">All</option>
                    {graphicDesigners.map(user => <option key={user.id} value={user.id}>{user.firstname} {user.lastname}</option>)}
                  </Select>
                </FormControl>
              )}
            </Flex>
            <UiDataTable
              mt={4}
              tableConfig={tableConfig}
              updateTableConfig={setTableConfig}
              rows={customizedRequests}
              rowKey="id"
              columns={ARTWORK_REQUEST_COLUMNS}
              isLoading={isLoading}
            />
          </UiCardBody>
        </UiCard>
      </Box>
      {disclosureConfirmation.isOpen && (
        <ModalConfirmation
          {...disclosureConfirmation}
          heading="Remove Artwork Request"
          description="Are you sure to remove the request?"
          onConfirm={() => handleRemove(selectedRequest?.id)}
          isConfirming={isRemoving}
        />
      )}
    </>
  )
}
